import { Box, Skeleton } from "@mui/material";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import React, { useEffect, useState } from "react";
import { useGroupEntitiesQuery } from "../../processingActivity/hooks/useGroupEntitiesQuery.ts";
import { useSelectableResponsiblesAssignees } from "../../annualWheel/useSelectableResponsiblesAssignees.ts";
import { DotLegalHeader, DotLegalInformationTooltip, DotLegalSwitch } from "@dotlegal/dotlegal-ui-components";
import LegalEntitySelector from "../../legalEntity/legalEntitySelector/LegalEntitySelector.tsx";
import { isNullOrWhitespace } from "../stringOperations.ts";
import { LegalEntitySelectorViewModel } from "../../legalEntity/LegalEntity.types.ts";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";

export interface GroupEntitySelectorProps {
    assignees?: Array<string> | undefined;
    responsible?: string | undefined;
    onGroupCompanyResponsibleChange?: (userId: string | null, groupCompanyId: string) => void;
    onGroupCompaniesChange: (groupCompanies: Array<string>) => void;
    selectedUserGroupCompanies: Array<{ userId: string | null; groupCompanyId: string }> | undefined;
    showError?: boolean;
    enableResponsibleColumn?: boolean;
    explanation?: string;
    header?: string;
    headerStyle?: "small" | "medium";
    switchLabel: string;
    // Shown under the switch and swapped as it toggles, so the consequence of each choice is spelled out.
    appliesToAllDescription?: string;
    appliesToSelectedDescription?: string;
    errorText?: string;
    disabled?: boolean;
    appliesToAllGroupEntities?: boolean;
    onAppliesToAllGroupEntitiesChange?: (appliesToAllGroupEntities: boolean) => void;
    toggleDisabled?: boolean;
    visibleToNonGdpo?: boolean;
    autoSelectSingleGroupEntity?: boolean;
    legalEntityItems?: Array<LegalEntitySelectorViewModel>;
    isLoading?: boolean;
    disableSelectAll?: boolean;
    /** A pane is narrow and already frames its content, so the section's border and padding only cost width there. */
    withoutSectionFrame?: boolean;
}

function GroupEntitySelector(props: GroupEntitySelectorProps) {
    const { gdpo } = useUserContext();
    const [internalAppliesToAllGroupEntities, setInternalAppliesToAllGroupEntities] = useState((props.selectedUserGroupCompanies ?? []).length === 0);
    let groupEntitiesQuery = useGroupEntitiesQuery(!props.legalEntityItems);
    // Responsibles feed the responsible column only, and not every user may fetch them: an unconditional
    // fetch gives users without the user-list permission a 403 that ejects them to the forbidden page.
    const responsiblesAssignees = useSelectableResponsiblesAssignees(props.assignees, props.responsible, undefined, props.enableResponsibleColumn === true);

    const appliesToAllGroupEntities = props.appliesToAllGroupEntities ?? internalAppliesToAllGroupEntities;
    const selectableEntities = props.legalEntityItems ?? groupEntitiesQuery.allGroupEntities;
    const isLoading = props.isLoading ?? groupEntitiesQuery.isLoading;
    const visibleToUser = gdpo || props.visibleToNonGdpo;
    const showSelector = (selectableEntities?.length ?? 0) > 1 && visibleToUser;

    useEffect(() => {
        if (props.autoSelectSingleGroupEntity && selectableEntities?.length === 1) {
            props.onGroupCompaniesChange(selectableEntities.map((x) => x.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectableEntities]);

    function onAppliesToAllGroupEntitiesToggle() {
        if (props.onAppliesToAllGroupEntitiesChange) {
            props.onAppliesToAllGroupEntitiesChange(!appliesToAllGroupEntities);
        } else {
            setInternalAppliesToAllGroupEntities(!appliesToAllGroupEntities);
            props.onGroupCompaniesChange([]);
        }
    }

    function getSwitchDescription() {
        return appliesToAllGroupEntities ? props.appliesToAllDescription : props.appliesToSelectedDescription;
    }

    const getErrorMessage = (error: string) => {
        if (error)
            return (
                <Box
                    sx={(theme) => ({
                        fontSize: 12,
                        color: theme.palette.error.main,
                    })}
                >
                    {error}
                </Box>
            );

        return undefined;
    };

    // The header belongs inside the guarded content: rendered by the caller it would stay behind on the surfaces
    // where the selector renders nothing, such as a customer with a single group entity.
    const header = !isNullOrWhitespace(props.header ?? null) && (
        <DotLegalHeader headerStyle={props.headerStyle ?? "small"} marginBottom={1}>
            {props.header}
        </DotLegalHeader>
    );
    const selectorContent = (
        <>
            <DotLegalSwitch
                disabled={props.disabled || props.toggleDisabled}
                onChange={onAppliesToAllGroupEntitiesToggle}
                checked={appliesToAllGroupEntities}
                rightLabel={
                    <Box component={"span"} sx={{ display: "inline-flex", gap: 1 }}>
                        {props.switchLabel}

                        {!isNullOrWhitespace(props.explanation ?? null) && <DotLegalInformationTooltip text={props.explanation} />}
                    </Box>
                }
                greyUncheckedTrack
            />
            {getSwitchDescription() && (
                <Box sx={(theme) => ({ fontSize: theme.typography.pxToRem(12), color: theme.palette.grey[600] })}>{getSwitchDescription()}</Box>
            )}
            {!appliesToAllGroupEntities && (
                <Box
                    sx={(theme) => ({
                        marginTop: 3,
                        "& .MuiAutocomplete-root .MuiInputBase-input": {
                            fontSize: theme.typography.pxToRem(12),
                        },
                        "& .MuiAutocomplete-root .MuiInputBase-input::placeholder": {
                            fontSize: theme.typography.pxToRem(12),
                        },
                    })}
                >
                    <LegalEntitySelector
                        disableSelectAll={props.disableSelectAll ?? props.disabled}
                        disabled={props.disabled}
                        marginTop={0}
                        allColumns={false}
                        isLoading={isLoading}
                        selectedEntities={props.selectedUserGroupCompanies?.map((x) => x.groupCompanyId) ?? []}
                        legalEntityItems={selectableEntities?.map((item) => {
                            return {
                                users: responsiblesAssignees.selectableResponsibles,
                                ...item,
                            };
                        })}
                        setSelectedEntities={(e) => props.onGroupCompaniesChange(e)}
                        hideGridSize
                        gridlength={10000}
                        enableResponsibleColumn={props.enableResponsibleColumn}
                        onResponsibleChange={props.onGroupCompanyResponsibleChange}
                        userGroupCompanies={props.selectedUserGroupCompanies ?? []}
                        mainResponsible={responsiblesAssignees.selectableResponsibles?.find((x) => x.id === props.responsible) ?? undefined}
                    />
                    {getErrorMessage(props.showError && !isNullOrWhitespace(props.errorText ?? null) ? props.errorText! : "")}
                </Box>
            )}
        </>
    );

    // Without a placeholder the section pops into the dialog once the query resolves, shifting everything above it.
    if (isLoading && visibleToUser) {
        return (
            <>
                {header}
                <DotLegalSection noBorder={props.withoutSectionFrame} noPadding={props.withoutSectionFrame}>
                    {[1, 2, 3, 4, 5].map((x) => (
                        <Skeleton key={x} variant="rectangular" sx={{ width: "100%", marginTop: 2 }} height={20} />
                    ))}
                </DotLegalSection>
            </>
        );
    }

    const loadedContent = (
        <>
            {header}
            <DotLegalSection noMargin noBorder={props.withoutSectionFrame} noPadding={props.withoutSectionFrame}>
                {selectorContent}
            </DotLegalSection>
        </>
    );
    return <>{showSelector && loadedContent}</>;
}

export default GroupEntitySelector;
